import { Grid, makeStyles, Button, Tooltip } from "@material-ui/core";
import React, { useCallback, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import ClipLoader from "react-spinners/ClipLoader";
import adManagerbackend from "../../../../../api/api3";
import MaterialTable from "../../../Mateial/MaterialTable";
import { formatPrice } from "../../../../../utils/formatPrice";
import { capitalize } from "@material-ui/core/utils";
import { global_actions } from "../../../../../redux-store";
import CreateAdModal from "./CreateAdModal";
import AdDetailsModal from "./AdDetailsModal"; // import the new modal component
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(
  "pk_test_51Mm4i5BbiNWHJzfiKwdLrGwuhvo9zqQDTsxtKTePi6iliH6VWYL4TEVFB3N3EnnmnGyp3dlldupuIiVsuC8C8F4K00snGFrutv"
);

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: "100%",
  },
  size: {
    [theme.breakpoints.down("lg")]: {
      width: "100%",
      marginLeft: theme.spacing(1),
    },
    [theme.breakpoints.down("md")]: {
      width: "95%",
      marginLeft: theme.spacing(5),
    },
    [theme.breakpoints.down("sm")]: {
      width: "90%",
      marginLeft: theme.spacing(3),
    },
    [theme.breakpoints.down("xs")]: {
      width: "80%",
      marginLeft: theme.spacing(6),
    },
  },
}));

const MyAds = () => {
  const [openModal, setOpenModal] = useState(false);
  const [openAdDetailsModal, setOpenAdDetailsModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null); // for storing selected ad
  const { user } = useSelector((state) => state.auth);
  const [ads, setAds] = useState([]);
  const [adsList, setAdsList] = useState([]);
  const { loading } = useSelector((state) => state.global);
  const dispatch = useDispatch();
  const classes = useStyles();

  React.useEffect(() => {
    if (user) {
      getMyAds();
    }
  }, [user]);

  const getMyAds = async () => {
    try {
      dispatch(global_actions.loading(true));
      const query = {
        created_by: user.user._id,
      };
      const {
        data: { data },
      } = await adManagerbackend.get(`/adworkshop`, {
        params: {
          ...query,
        },
      });
      setAds(data);
      setAdsList(data);
    } catch (err) {
    } finally {
      dispatch(global_actions.loading(false));
    }
  };

  const handleFilterChange = (e) => {
    const filter = e.target.value;
    if (filter === "pending") {
      const filtered = ads.filter(
        (ad) => ad.status.slice(-1)[0].status === "pending"
      );
      return setAdsList(filtered);
    }
    if (filter === "approved") {
      const filtered = ads.filter(
        (ad) => ad.status.slice(-1)[0].status === "approved"
      );
      return setAdsList(filtered);
    }
    if (filter === "declined") {
      const filtered = ads.filter(
        (ad) => ad.status.slice(-1)[0].status === "declined"
      );
      return setAdsList(filtered);
    }

    return setAdsList(ads);
  };

  const handleOpen = () => {
    setOpenModal(true);
  };

  const handleClose = useCallback(() => {
    setOpenModal(false);
  }, [openModal]);

  const handleOpenAdDetails = (ad) => {
    setSelectedAd(ad);
    setOpenAdDetailsModal(true);
  };

  const handleCloseAdDetails = () => {
    setSelectedAd(null);
    setOpenAdDetailsModal(false);
  };

  const getExpiredStatus = (ad) => {
    const { expires_at, is_charged, expired } = ad;
    if (!expires_at && !is_charged) {
      return "Not Placed";
    } else if (expires_at && is_charged && !expired) {
      return "In Progress";
    } else if (expired) {
      return "Expired";
    }
    return "-";
  };

  const columns = [
    {
      title: "Ad ID",
      field: "ad_id",
    },
    {
      title: "Retail Value",
      field: "retailValueOfPublication",
      render: (rowData) => {
        const { minimum, maximum } = rowData.retailValueOfPublication || {};
        if (minimum && maximum) {
          return `$${minimum} - $${maximum}`;
        } else if (maximum) {
          return `$${maximum} - below`;
        } else if (minimum) {
          return `$${minimum} - above`;
        }
        return "-";
      },
    },
    {
      title: "Ad Level",
      field: "ad_level",
    },
    {
      title: "Price/ad",
      field: "price",
      render: (rowData) => formatPrice(rowData.price),
    },
    {
      title: "Expired",
      field: "expired",
      render: (rowData) => getExpiredStatus(rowData),
    },
    {
      title: "Status",
      field: "status",
      render: (rowData) => capitalize(rowData.status),
    },
    {
      title: "Actions",
      field: "actions",
      render: (rowData) => (
        <Tooltip title="View Details">
          <Button
            variant="contained"
            color="primary"
            style={{ color: "white", borderRadius: "0px" }}
            onClick={() => handleOpenAdDetails(rowData)} // open modal with ad details
          >
            View
          </Button>
        </Tooltip>
      ),
    },
  ];

  const data = adsList.map((ad) => ({
    ad_id: ad.ad_id,
    retailValueOfPublication: ad.retailValueOfPublication,
    ad_level: ad.ad_level,
    price: ad.ad_price,
    expired: ad.expired,
    status: capitalize(ad.status.slice(-1)[0].status),
    is_charged: ad.is_charged,
    expires_at: ad.expires_at,
    ad_file: ad.ad_file,
    createdAt: ad.createdAt,
    updatedAt: ad.updatedAt,
  }));

  return (
    <div>
      <Helmet>
        <title>eksplode! | Ads Workshop</title>
      </Helmet>
      <div className="dashboard">
        <div className="box-head">
          <h2>My Ads</h2>
        </div>
        <div className="row mt-3 justify-content-end mb-2">
          <button className="btn btn-solid" onClick={() => handleOpen()}>
            Create Ad
          </button>
        </div>
        <div className="form-row justify-content-end d-flex">
          <div className="col-md-6">
            <div className="form-group">
              <select
                className="form-control mb-0"
                onChange={(e) => handleFilterChange(e)}
              >
                <option selected value="">
                  Apply Filter
                </option>
                <option value="pending">Pending</option>
                <option value="declined">Declined</option>
                <option value="approved">Approved</option>
              </select>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="my-5 row justify-content-center">
            <ClipLoader color={"#000000"} loading={loading} size={60} />
          </div>
        ) : (
          <div className={classes.root}>
            <Grid container className={classes.container}>
              <Grid
                item
                lg={12}
                sm={12}
                md={12}
                xs={12}
                className={classes.table}
              >
                <div className={classes.size}>
                  <MaterialTable
                    title={"My Ads"}
                    columns={columns}
                    data={data}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        )}
      </div>
      {/* Ad Details Modal */}
      {selectedAd && (
        <AdDetailsModal
          open={openAdDetailsModal}
          close={handleCloseAdDetails}
          ad={selectedAd} // pass the selected ad details
        />
      )}
      {openModal && (
         <Elements stripe={stripePromise}>
           <CreateAdModal
             open={openModal}
             close={() => handleClose()}
             getAds={() => getMyAds()}
           />
         </Elements>
      )}
    </div>
  );
};

export default MyAds;
